import { Dispatch, Fragment, SetStateAction } from 'react';
import { useSession } from '@sdk/session/SessionContext';
import Tooltip from '@sdk/common/tooltip/Tooltip';

import { Message } from '@web/conversations/Message/Message';
import { MessageDateSeparator } from '@web/conversations/Message/MessageDateSeparator/MessageDateSeparator';

import {
  useConversationMessages,
  useScrollToMarkedMessage,
  useUnreadMarker,
} from './conversationMessages.hooks';
import { DummyAnchor, ItemWrapper, NewMessagesSeparator } from './conversationMessages.styles';

type Props = {
  setFirstUnreadMessageAnchor: Dispatch<SetStateAction<HTMLDivElement>>;
  setNewestMessageAnchor: Dispatch<SetStateAction<HTMLDivElement>>;
};

export const ConversationMessages = ({
  setFirstUnreadMessageAnchor,
  setNewestMessageAnchor,
}: Props) => {
  const {
    currentUser: {
      settings: { usTimeFormat },
    },
  } = useSession();
  useUnreadMarker();
  const { getUIFlags, visibleMessages } = useConversationMessages();
  const markedMessageRef = useScrollToMarkedMessage();

  return (
    <>
      {visibleMessages.map((message, index) => {
        const {
          isFirstUnread,
          isHighlighted,
          isNewest,
          isMarked,
          withDateSeparator,
          withHeader,
          withNewSeparator,
        } = getUIFlags(index);
        const ref = isMarked ? markedMessageRef : undefined;
        const hasSeparators = withDateSeparator || withNewSeparator;

        return (
          <Fragment key={message.id}>
            {hasSeparators && (
              <ItemWrapper extraSpacing>
                {withDateSeparator && (
                  <MessageDateSeparator date={message.postedAt} usTimeFormat={usTimeFormat} />
                )}
                {withNewSeparator && <NewMessagesSeparator />}
              </ItemWrapper>
            )}

            {isFirstUnread && <DummyAnchor ref={setFirstUnreadMessageAnchor} />}
            {isNewest && <DummyAnchor ref={setNewestMessageAnchor} />}

            <ItemWrapper highlight={isHighlighted} ref={ref}>
              <Message message={message} withHeader={withHeader} />
            </ItemWrapper>
          </Fragment>
        );
      })}

      <Tooltip attachToBody id="edited-date-label-tooltip" />
    </>
  );
};
